import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Copy, ExternalLink, Video, Users, MessageCircle, Sparkles, Calendar, ArrowLeft, Zap, Play, Download, FileText } from "lucide-react";
import AutomationBuilder from "@/components/AutomationBuilder";
import UtmBuilder from "@/components/UtmBuilder";
import LiveAnalytics from "@/components/live/LiveAnalytics";

const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export default function WebinarDetail() {
  const { id } = useParams();
  const [w, setW] = useState(null);
  const [regs, setRegs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [scheduled, setScheduled] = useState({ items: [], counts: {} });

  const load = async () => {
    const { data } = await api.get(`/webinars/${id}`);
    setW(data);
    const rs = await api.get(`/webinars/${id}/registrants`);
    setRegs(rs.data);
    const ms = await api.get(`/webinars/${id}/messages`);
    setMsgs(ms.data);
    try {
      const sc = await api.get(`/webinars/${id}/scheduled`);
      setScheduled(sc.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, [id]);

  const copyLink = () => {
    const url = `${window.location.origin}/w/${w.slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Public link copied to clipboard");
  };

  const publish = async () => {
    await api.post(`/webinars/${id}/publish`);
    toast.success("Webinar published");
    load();
  };

  if (!w) return <div className="text-[#4A4D57]">Loading…</div>;

  const registrations = regs.length;
  const attended = regs.filter((r) => r.attended).length;
  const paid = regs.filter((r) => r.payment_status === "paid").length;
  const revenue = regs.filter((r) => r.payment_status === "paid").reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <div className="space-y-6 vp-fade-up">
      <Link to="/app/webinars" className="text-sm text-[#4A4D57] inline-flex items-center gap-1 hover:text-[#0B5CFF]">
        <ArrowLeft className="w-4 h-4" /> All webinars
      </Link>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="vp-label">{w.status}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">{w.title}</h1>
          <div className="text-sm text-[#4A4D57] mt-1">
            {new Date(w.scheduled_at).toLocaleString("en-IN")} · {w.duration_min} min · {w.price > 0 ? fmtINR(w.price) : "Free"}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={copyLink} data-testid={TIDS.webinarCopyLink} className="vp-btn-ghost inline-flex items-center gap-2">
            <Copy className="w-4 h-4" /> Copy link
          </button>
          <a href={`/w/${w.slug}`} target="_blank" rel="noreferrer" className="vp-btn-ghost inline-flex items-center gap-2">
            <ExternalLink className="w-4 h-4" /> Preview
          </a>
          {w.status !== "published" && (
            <button onClick={publish} data-testid={TIDS.webinarPublish} className="vp-btn-primary">Publish</button>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Users} tint="#2C6EFF" label="Registrations" value={registrations} />
        <Kpi icon={Users} tint="#0B5CFF" label="Attended" value={`${attended}`} />
        <Kpi icon={MessageCircle} tint="#0B5CFF" label="Paid" value={paid} />
        <Kpi icon={Video} tint="#0B5CFF" label="Revenue" value={fmtINR(revenue)} />
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-[#FFFFFF]/60 border border-[#E4E6EC] rounded-full p-1 flex flex-wrap gap-1 h-auto">
          {["overview", "registrants", "automation", "content", "landing", "live", "recording", "analytics"].map((k) => (
            <TabsTrigger key={k} value={k} className="rounded-full px-4 py-1.5 data-[state=active]:bg-[#0B5CFF] data-[state=active]:text-[#0B0D17] text-[#4A4D57] text-sm capitalize">
              {k}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid lg:grid-cols-3 gap-5">
            <div className="vp-glass p-6 lg:col-span-2 space-y-3">
              <div className="vp-label">About</div>
              <p className="text-[#0B0D17] leading-relaxed">{w.description}</p>
              <div className="vp-divider" />
              <div className="vp-label">Problem tags</div>
              <div className="flex flex-wrap gap-2">
                {(w.problem_tags || []).map((t) => (<span key={t} className="vp-chip">{t}</span>))}
                {(w.problem_tags || []).length === 0 && <span className="text-sm text-[#747680]">No tags</span>}
              </div>
            </div>
            <div className="vp-glass p-6">
              <div className="vp-label">Reminder schedule</div>
              <ul className="mt-3 space-y-3">
                {(w.reminder_schedule || []).map((r) => (
                  <li key={r.key} className="flex items-center gap-3 text-sm">
                    <Calendar className="w-4 h-4 text-[#0B5CFF]" strokeWidth={1.5} />
                    <span className="text-[#0B0D17]">{r.key}</span>
                    <span className="text-[#747680] text-xs ml-auto">{new Date(r.run_at).toLocaleString("en-IN")}</span>
                  </li>
                ))}
              </ul>
            </div>
            {w.price > 0 && (
              <div className="lg:col-span-3">
                <CouponManager webinarId={w.id} />
              </div>
            )}
            <div className="lg:col-span-3">
              <UtmBuilder slug={w.slug} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="registrants" className="mt-6">
          <RegistrantsTable rows={regs} />
        </TabsContent>

        <TabsContent value="automation" className="mt-6">
          <AutomationBuilder w={w} msgs={msgs} scheduled={scheduled} onReload={load} />
        </TabsContent>

        <TabsContent value="content" className="mt-6">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <a
              href={`${API_BASE}/webinars/${w.id}/speaker-deck.pdf`}
              target="_blank" rel="noreferrer"
              data-testid="download-deck-btn"
              className="vp-btn-primary inline-flex items-center gap-2"
            >
              <Download className="w-4 h-4" strokeWidth={1.75} /> Download speaker deck
            </a>
            <span className="text-sm text-[#4A4D57]">
              <FileText className="w-4 h-4 inline mr-1" strokeWidth={1.5} />
              16-slide PDF, auto-composed from the landing copy + agenda
            </span>
          </div>
          <ContentEditor w={w} onSaved={load} />
        </TabsContent>

        <TabsContent value="landing" className="mt-6">
          <div className="vp-glass p-6">
            <div className="vp-label">Public landing page</div>
            <div className="mt-2 text-[#4A4D57] text-sm break-all">{window.location.origin}/w/{w.slug}</div>
            <div className="mt-4 flex gap-2 flex-wrap">
              <button onClick={copyLink} className="vp-btn-primary">Copy link</button>
              <a href={`/w/${w.slug}`} target="_blank" rel="noreferrer" className="vp-btn-ghost">Open in new tab</a>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="live" className="mt-6">
          <div className="vp-glass p-6 space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="vp-label">Native live studio (powered by 100ms)</div>
                <div className="text-sm text-[#0B0D17] mt-1">Host the webinar directly in-browser — attendees join right on your page, no external app required.</div>
              </div>
              <a
                href={`/app/webinars/${w.id}/live-room`}
                className="vp-btn-primary inline-flex items-center gap-2"
                data-testid="go-live-btn"
              >
                🎥 Go Live
              </a>
            </div>
            <div className="border-t border-[#E4E6EC] pt-3 text-xs text-[#747680]">
              Every registrant gets a personal 100ms watch link (<span className="font-mono">/w/{w.slug}/live?rid=…</span>) with Q&A, polls, chat, hand-raise and reactions. No external app needed — attendees join on your domain.
            </div>
          </div>
        </TabsContent>

        <TabsContent value="recording" className="mt-6">
          <RecordingUploader w={w} onSaved={load} />
        </TabsContent>

        <TabsContent value="analytics" className="mt-6 space-y-8">
          <LiveAnalytics webinarId={w.id} />
          <div className="border-t border-[#E4E6EC] pt-6">
            <h3 className="font-display font-semibold text-sm mb-3 text-[#4A4D57]">Registration funnel</h3>
            <PerWebinarAnalytics webinarId={w.id} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Kpi({ icon: Icon, tint, label, value }) {
  return (
    <div className="vp-kpi">
      <div className="w-9 h-9 rounded-xl border border-[#E4E6EC] flex items-center justify-center" style={{ background: "rgba(11,92,255,0.15)" }}>
        <Icon className="w-4 h-4" style={{ color: tint }} strokeWidth={1.5} />
      </div>
      <div className="mt-4 font-display text-2xl font-bold">{value}</div>
      <div className="vp-label mt-1">{label}</div>
    </div>
  );
}

function RegistrantsTable({ rows }) {
  const download = () => {
    const csv = ["Name,Email,WhatsApp,City,Payment,Amount,Attended,Created"]
      .concat(rows.map((r) => [r.name, r.email, `${r.country_code}${r.whatsapp}`, r.city || "", r.payment_status, r.amount, r.attended, r.created_at].join(",")))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "registrants.csv"; a.click();
  };
  return (
    <div className="vp-glass p-4 overflow-x-auto">
      <div className="flex items-center justify-between px-2 pb-3">
        <div className="text-sm text-[#4A4D57]">{rows.length} registrant{rows.length === 1 ? "" : "s"}</div>
        <button onClick={download} className="vp-btn-ghost text-xs py-1.5 px-3">Export CSV</button>
      </div>
      <table className="vp-table min-w-[800px]">
        <thead><tr><th>Name</th><th>Email</th><th>WhatsApp</th><th>City</th><th>Payment</th><th>Attended</th></tr></thead>
        <tbody>
          {rows.length === 0 && (<tr><td colSpan={6} className="text-[#747680]">No registrations yet. Share your landing page link.</td></tr>)}
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="text-[#0B0D17]">{r.name}</td>
              <td>{r.email}</td>
              <td>{r.country_code}{r.whatsapp}</td>
              <td>{r.city || "—"}</td>
              <td><span className="vp-chip">{r.payment_status}</span></td>
              <td>{r.attended ? "Yes" : "No"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AutomationList({ w, msgs, scheduled, onReload }) {
  const rules = [
    { key: "confirmation", when: "Immediately on registration", channels: ["email", "whatsapp"] },
    { key: "reminder_24h", when: "24 hours before start", channels: ["email", "whatsapp"] },
    { key: "reminder_1h", when: "1 hour before start", channels: ["email", "whatsapp"] },
    { key: "reminder_10min", when: "10 minutes before start", channels: ["email", "whatsapp"] },
    { key: "missed_it", when: "5 min after end · no-shows", channels: ["email", "whatsapp"] },
    { key: "thank_you", when: "5 min after end · attendees", channels: ["email", "whatsapp"] },
  ];

  // Count scheduled per key
  const countsByKey = (scheduled.items || []).reduce((acc, r) => {
    acc[r.key] = acc[r.key] || { pending: 0, sent: 0, skipped: 0, failed: 0 };
    acc[r.key][r.status] = (acc[r.key][r.status] || 0) + 1;
    return acc;
  }, {});

  const fireNow = async (key) => {
    try {
      const { data } = await api.post(`/webinars/${w.id}/fire-now/${key}`);
      toast.success(`Fired ${data.fired} message${data.fired === 1 ? "" : "s"}`);
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-4 gap-3">
        <ReminderKpi label="Pending" value={scheduled.counts?.pending ?? 0} tint="#0B5CFF" />
        <ReminderKpi label="Sent" value={scheduled.counts?.sent ?? 0} tint="#2C6EFF" />
        <ReminderKpi label="Skipped" value={scheduled.counts?.skipped ?? 0} tint="#747680" />
        <ReminderKpi label="Failed" value={scheduled.counts?.failed ?? 0} tint="#0B5CFF" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-3">
          {rules.map((r) => {
            const c = countsByKey[r.key] || {};
            const isPre = r.key.startsWith("reminder_") || r.key === "confirmation";
            return (
              <div key={r.key} className="vp-glass p-5 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#0B5CFF]/20 border border-[#0B5CFF]/40 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-[#0B5CFF]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-display font-semibold capitalize">{r.key.replace(/_/g, " ")}</div>
                      <div className="text-xs text-[#747680] mt-0.5">{r.when} · via {r.channels.join(" + ")}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {c.pending > 0 && <span className="vp-chip text-[#0B5CFF]">{c.pending} pending</span>}
                      {c.sent > 0 && <span className="vp-chip text-[#1E8E3E]">{c.sent} sent</span>}
                      {r.key !== "confirmation" && c.pending > 0 && (
                        <button
                          onClick={() => fireNow(r.key)}
                          data-testid={`fire-now-${r.key}`}
                          className="vp-btn-ghost text-xs py-1.5 px-3 inline-flex items-center gap-1"
                        >
                          <Play className="w-3 h-3" /> Fire now
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-[#0B0D17] mt-3 whitespace-pre-wrap">
                    {w.email_templates?.[r.key]?.body || w.whatsapp_templates?.[r.key] || ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="vp-glass p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="vp-label">Message log</div>
            <Zap className="w-4 h-4 text-[#0B5CFF]" strokeWidth={1.5} />
          </div>
          <ul className="space-y-3 max-h-[500px] overflow-auto">
            {msgs.length === 0 && <li className="text-sm text-[#747680]">No messages sent yet.</li>}
            {msgs.map((m) => (
              <li key={m.id} className="text-sm border border-[#E4E6EC] rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <span className="vp-chip">{m.channel}</span>
                  <span className="text-xs text-[#747680]">{new Date(m.sent_at).toLocaleString("en-IN")}</span>
                </div>
                <div className="text-[#0B0D17] mt-2">to <span className="text-[#4A4D57]">{m.to}</span></div>
                <div className="text-xs text-[#747680] mt-1">{m.tag || m.subject || m.template}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ReminderKpi({ label, value, tint }) {
  return (
    <div className="vp-glass p-4">
      <div className="vp-label">{label}</div>
      <div className="font-display text-2xl font-bold mt-1" style={{ color: tint }}>{value}</div>
    </div>
  );
}

function PerWebinarAnalytics({ webinarId }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/analytics/overview?webinar_id=${webinarId}&days=30`).then((r) => setD(r.data));
  }, [webinarId]);
  if (!d) return <div className="text-sm text-[#747680]">Loading…</div>;
  const conv = d.funnel.visits ? ((d.funnel.registrations / d.funnel.visits) * 100).toFixed(1) : 0;
  return (
    <div className="grid md:grid-cols-4 gap-4">
      <div className="vp-glass p-5"><div className="vp-label">Visits</div><div className="font-display text-3xl font-medium mt-1">{d.funnel.visits}</div></div>
      <div className="vp-glass p-5"><div className="vp-label">Registrations</div><div className="font-display text-3xl font-medium mt-1 text-[#0B5CFF]">{d.funnel.registrations}</div></div>
      <div className="vp-glass p-5"><div className="vp-label">Paid</div><div className="font-display text-3xl font-medium mt-1 text-[#1E8E3E]">{d.funnel.paid}</div></div>
      <div className="vp-glass p-5"><div className="vp-label">Visit → Register</div><div className="font-display text-3xl font-medium mt-1 text-[#0B5CFF]">{conv}%</div></div>
      <div className="md:col-span-4 text-sm text-[#747680]">
        Full funnel + revenue timeline + source breakdown live in the sidebar <Link to="/app/analytics" className="text-[#0B5CFF] hover:underline">Analytics</Link> page.
      </div>
    </div>
  );
}

function ContentEditor({ w, onSaved }) {
  const [copy, setCopy] = useState(JSON.stringify(w.landing_copy || {}, null, 2));
  const [emails, setEmails] = useState(JSON.stringify(w.email_templates || {}, null, 2));
  const [waTpl, setWaTpl] = useState(JSON.stringify(w.whatsapp_templates || {}, null, 2));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        landing_copy: JSON.parse(copy),
        email_templates: JSON.parse(emails),
        whatsapp_templates: JSON.parse(waTpl),
      };
      await api.patch(`/webinars/${w.id}`, body);
      toast.success("Content saved");
      onSaved();
    } catch (e) {
      toast.error("Invalid JSON: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const Box = ({ title, val, setVal }) => (
    <div className="vp-glass p-4">
      <div className="vp-label mb-2">{title}</div>
      <textarea rows={10} className="vp-input font-mono text-xs" value={val} onChange={(e) => setVal(e.target.value)} />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-3 gap-4">
        <Box title="Landing copy JSON" val={copy} setVal={setCopy} />
        <Box title="Email templates JSON" val={emails} setVal={setEmails} />
        <Box title="WhatsApp templates JSON" val={waTpl} setVal={setWaTpl} />
      </div>
      <button onClick={save} disabled={saving} className="vp-btn-primary">
        {saving ? "Saving…" : "Save content"}
      </button>
    </div>
  );
}


function CouponManager({ webinarId }) {
  const [rows, setRows] = useState([]);
  const [code, setCode] = useState("");
  const [kind, setKind] = useState("percent");
  const [value, setValue] = useState(10);
  const [maxUses, setMaxUses] = useState("");
  const [creating, setCreating] = useState(false);

  const load = () => api.get(`/webinars/${webinarId}/coupons`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [webinarId]); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post(`/webinars/${webinarId}/coupons`, {
        code: code.trim().toUpperCase(),
        kind,
        value: Number(value),
        max_uses: maxUses ? Number(maxUses) : null,
      });
      toast.success("Coupon created");
      setCode(""); setValue(10); setMaxUses("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setCreating(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete coupon?")) return;
    await api.delete(`/coupons/${id}`);
    load();
  };

  return (
    <div className="vp-card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="vp-label">Coupons</div>
          <div className="font-display text-xl font-semibold mt-1">Discount codes for this webinar</div>
        </div>
      </div>

      <form onSubmit={create} className="grid sm:grid-cols-5 gap-3 mb-4">
        <input className="vp-input uppercase" placeholder="CODE" required value={code} onChange={(e) => setCode(e.target.value)} data-testid="coupon-code-input" />
        <select className="vp-input" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="coupon-kind-select">
          <option value="percent">% off</option>
          <option value="flat">₹ flat off</option>
        </select>
        <input type="number" className="vp-input" min="1" required value={value} onChange={(e) => setValue(e.target.value)} placeholder={kind === "percent" ? "10" : "50"} data-testid="coupon-value-input" />
        <input type="number" className="vp-input" min="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="Max uses (opt)" data-testid="coupon-max-input" />
        <button type="submit" disabled={creating} data-testid="coupon-create-btn" className="vp-btn-primary">
          {creating ? "…" : "Create"}
        </button>
      </form>

      {rows.length === 0 ? (
        <div className="text-sm text-[#747680]">No coupons yet. Create one above to boost conversion.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="vp-table min-w-[560px]">
            <thead><tr><th>Code</th><th>Discount</th><th>Uses</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td className="font-mono text-[#0B5CFF]">{c.code}</td>
                  <td>{c.kind === "percent" ? `${c.value}% off` : `₹${c.value} off`}</td>
                  <td>{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                  <td><span className="vp-chip vp-chip-accent">{c.active ? "Active" : "Inactive"}</span></td>
                  <td className="text-right">
                    <button onClick={() => del(c.id)} data-testid={`coupon-del-${c.code}`} className="vp-btn-ghost text-xs py-1 px-3">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RecordingUploader({ w, onSaved }) {
  const [url, setUrl] = useState(w.recording_url || "");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/webinars/${w.id}/recording`, { recording_url: url });
      toast.success(`Recording saved · ${data.notified} attendee(s) notified`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <form onSubmit={save} className="vp-card p-6 lg:col-span-2 space-y-4">
        <div className="vp-label">Recording delivery</div>
        <div className="font-display text-xl font-bold">Paste the webinar recording URL</div>
        <p className="text-sm text-[#4A4D57]">Attendees will see a "Watch recording" button on their portal instantly. Paid attendees also get a fresh email + WhatsApp with the link.</p>
        <input type="url" className="vp-input" required value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://us02web.zoom.us/rec/share/…" data-testid="recording-url-input" />
        <div className="flex gap-3">
          <button type="submit" disabled={saving || !url} data-testid="recording-save-btn" className="vp-btn-primary">
            {saving ? "Saving…" : (w.recording_url ? "Update recording" : "Save & notify")}
          </button>
          {w.recording_url && (
            <a href={w.recording_url} target="_blank" rel="noreferrer" className="vp-btn-ghost inline-flex items-center gap-2">
              <ExternalLink className="w-4 h-4" /> Preview
            </a>
          )}
        </div>
      </form>
      <div className="vp-card p-6">
        <div className="vp-label mb-2">Status</div>
        {w.recording_url ? (
          <>
            <div className="flex items-center gap-2 text-[#1E8E3E] font-semibold text-sm">
              <span className="w-2 h-2 rounded-full bg-[#1E8E3E]" /> Recording live
            </div>
            <div className="text-xs text-[#747680] mt-1 break-all">{w.recording_url}</div>
            {w.recording_uploaded_at && <div className="text-xs text-[#747680] mt-2">Uploaded {new Date(w.recording_uploaded_at).toLocaleString("en-IN")}</div>}
          </>
        ) : (
          <div className="text-sm text-[#4A4D57]">No recording yet. In live mode this is wired to Zoom's <code>recording.completed</code> webhook — no manual step needed.</div>
        )}
      </div>
    </div>
  );
}

